import React, { useState } from 'react';
import { Container, Typography, Grid, Card, CardContent, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { keyframes } from '@emotion/react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ComputerIcon from '@mui/icons-material/Computer';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import RouterIcon from '@mui/icons-material/Router';
import SecurityIcon from '@mui/icons-material/Security';
import WifiIcon from '@mui/icons-material/Wifi';
import StorageIcon from '@mui/icons-material/Storage';
import WebIcon from '@mui/icons-material/Web';
import CloudIcon from '@mui/icons-material/Cloud';
import ShieldIcon from '@mui/icons-material/Shield';
import InstallDesktopIcon from '@mui/icons-material/InstallDesktop';
import DevicesIcon from '@mui/icons-material/Devices';
import PhoneIcon from '@mui/icons-material/Phone';
import DnsIcon from '@mui/icons-material/Dns';
import BusinessIcon from '@mui/icons-material/Business';
import LaptopIcon from '@mui/icons-material/Laptop';
import CodeIcon from '@mui/icons-material/Code';

// ── Keyframe animations ──────────────────────────────────────────────────────

const rotateCW = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const rotateCCW = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(-360deg); }
`;

const glowPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 25px rgba(255,107,107,0.25), inset 0 0 20px rgba(255,107,107,0.06);
    border-color: rgba(255,107,107,0.35);
  }
  50% {
    box-shadow: 0 0 60px rgba(255,107,107,0.5), 0 0 100px rgba(255,107,107,0.2),
                inset 0 0 40px rgba(255,107,107,0.12);
    border-color: rgba(255,107,107,0.7);
  }
`;

const ringPulse = keyframes`
  0%, 100% { border-color: rgba(255,107,107,0.13); }
  50%       { border-color: rgba(255,107,107,0.3); }
`;

const floatBadge = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-9px); }
`;

const badgeGlow = keyframes`
  0%, 100% { box-shadow: 0 4px 12px rgba(255,107,107,0.1); }
  50%       { box-shadow: 0 4px 20px rgba(255,107,107,0.3); }
`;

const rippleOut = keyframes`
  0%   { transform: scale(0.8);  opacity: 0.5; }
  100% { transform: scale(1.55); opacity: 0;   }
`;

const scaleBreath = keyframes`
  0%, 100% { transform: scale(1); }
  50%       { transform: scale(1.07); }
`;

const iconFloat = keyframes`
  0%, 100% {
    transform: translateY(0) scale(1);
    filter: drop-shadow(0 0 10px rgba(255,107,107,0.5));
  }
  50% {
    transform: translateY(-6px) scale(1.1);
    filter: drop-shadow(0 0 24px rgba(255,107,107,0.95)) drop-shadow(0 0 44px rgba(255,217,61,0.4));
  }
`;

const orbitDotPulse = keyframes`
  0%, 100% { opacity: 0.7; box-shadow: 0 0 4px 1px rgba(255,107,107,0.7); }
  50%       { opacity: 1;   box-shadow: 0 0 10px 4px rgba(255,107,107,1), 0 0 20px 2px rgba(255,217,61,0.4); }
`;

// ── Data ─────────────────────────────────────────────────────────────────────

const categories = [
  {
    title: 'Enterprise IT',
    description: 'Cloud infrastructure, server administration, endpoint management, and enterprise communication systems.',
    icon: BusinessIcon,
    services: [
      {
        title: 'Microsoft 365 & Azure Cloud',
        description: 'M365 setup, Azure AD, Exchange Online, SharePoint, Teams, Intune, and hybrid cloud administration.',
        icon: CloudIcon,
        path: '/microsoft-365-cloud',
      },
      {
        title: 'Endpoint Management',
        description: 'Microsoft Intune, Windows Autopilot, device compliance policies, and MDM for your organization.',
        icon: DevicesIcon,
        path: '/endpoint-management',
      },
      {
        title: 'Server Administration',
        description: 'Windows/Linux servers, VMware vSphere/ESXi, Proxmox, Active Directory, and Group Policy management.',
        icon: DnsIcon,
        path: '/server-administration',
      },
      {
        title: 'VoIP & Phone Systems',
        description: 'Cisco CUCM and Alcatel 4400/OXO installation, configuration, call routing, and QoS optimization.',
        icon: PhoneIcon,
        path: '/voip-systems',
      },
    ],
  },
  {
    title: 'Security & Networking',
    description: 'Protect and connect your business with enterprise-grade security and network infrastructure.',
    icon: ShieldIcon,
    services: [
      {
        title: 'Cybersecurity Services',
        description: 'CompTIA CySA+ certified. Firewall config (ASA/Pfsense/SonicWall), Zero Trust, NAC, and network security assessments.',
        icon: ShieldIcon,
        path: '/cybersecurity',
      },
      {
        title: 'Network Security',
        description: 'Advanced security measures to protect your network from unauthorized access.',
        icon: SecurityIcon,
        path: '/network-security',
      },
      {
        title: 'Network Installation',
        description: 'Professional setup of wired and wireless networks for homes and businesses.',
        icon: RouterIcon,
        path: '/network-installation',
      },
      {
        title: 'WiFi Optimization',
        description: 'Expert configuration for maximum coverage and performance of your wireless network.',
        icon: WifiIcon,
        path: '/wifi-optimization',
      },
      {
        title: 'Network Storage',
        description: 'Setup and configuration of network-attached storage (NAS) solutions.',
        icon: StorageIcon,
        path: '/network-storage',
      },
    ],
  },
  {
    title: 'Device Repair',
    description: 'Professional hardware repair for laptops, Macs, desktops, phones, tablets, servers, and data recovery.',
    icon: LaptopIcon,
    path: '/device-repair',
    services: [
      {
        title: 'Laptop Repair',
        description: 'All PC laptop brands — screen, battery, keyboard, motherboard, liquid damage, and OS issues.',
        icon: ComputerIcon,
        path: '/laptop-repair',
      },
      {
        title: 'Mac Repair',
        description: 'MacBook Air, MacBook Pro, iMac, Mac mini — hardware repair, upgrades, and macOS recovery.',
        icon: LaptopIcon,
        path: '/mac-repair',
      },
      {
        title: 'Desktop Service',
        description: 'PC desktop diagnostics, hardware upgrades, virus removal, OS installation, and custom builds.',
        icon: ComputerIcon,
        path: '/desktop-service',
      },
      {
        title: 'Smartphone Repair',
        description: 'iPhone, Samsung, Google, and all Android phones — screen, battery, charging port, water damage.',
        icon: PhoneIphoneIcon,
        path: '/mobile-repair',
      },
      {
        title: 'Tablet Repair',
        description: 'iPad, Samsung Tab, Surface, and Android tablets — screen, battery, software, and hardware repair.',
        icon: PhoneIphoneIcon,
        path: '/tablet-repair',
      },
      {
        title: 'Server Hardware Repair',
        description: 'Rack and tower servers, NAS, workstations — RAID rebuild, PSU, RAM, storage, and cooling repair.',
        icon: StorageIcon,
        path: '/server-repair',
      },
      {
        title: 'Data Recovery',
        description: 'Professional data recovery from failed hard drives, SSDs, RAID arrays, and flash storage.',
        icon: StorageIcon,
        path: '/data-recovery',
      },
      {
        title: 'Parts Replacement',
        description: 'Genuine replacement parts for laptops, desktops, servers, and mobile devices — all brands.',
        icon: ComputerIcon,
        path: '/parts-replacement',
      },
    ],
  },
  {
    title: 'Software & Support',
    description: 'From OS deployment to web development — complete software setup and ongoing technical support.',
    icon: CodeIcon,
    services: [
      {
        title: 'Software Installation & Configuration',
        description: 'OS deployment (Windows/Linux/macOS), application setup, automated provisioning, and system configuration.',
        icon: InstallDesktopIcon,
        path: '/software-installation',
      },
      {
        title: 'Technical Support',
        description: 'Tier 1-3 technical support for all your hardware and software issues, remote or on-site.',
        icon: SupportAgentIcon,
        path: '/technical-support',
      },
      {
        title: 'Website Development',
        description: 'Custom website design and development services for businesses and individuals.',
        icon: WebIcon,
        path: '/website-development',
      },
    ],
  },
];

const badges = [
  { icon: ShieldIcon,   top: '-2%',  right: '4%',   left: undefined,  bottom: undefined, label: 'Cybersecurity',       route: '/cybersecurity',       delay: '0s' },
  { icon: CloudIcon,    bottom: '10%', right: '-10%', left: undefined,  top: undefined,    label: 'Microsoft-365-cloud', route: '/microsoft-365-cloud', delay: '0.8s' },
  { icon: RouterIcon,   top: '26%',  left: '-14%',  right: undefined, bottom: undefined, label: 'Network Engineering', route: '/network-installation', delay: '1.6s' },
  { icon: BusinessIcon, bottom: '2%', left: '10%',  right: undefined, top: undefined,    label: 'Enterprise IT',       route: '/endpoint-management',  delay: '2.4s' },
  { icon: LaptopIcon,   top: '38%',  right: '-14%', left: undefined,  bottom: undefined, label: 'Device Repair',       route: '/device-repair',        delay: '3.2s' },
];

// ── Shared card style ─────────────────────────────────────────────────────────

const cardStyle = {
  height: '100%',
  background: 'rgba(255, 107, 107, 0.05)',
  backdropFilter: 'blur(10px)',
  borderRadius: '16px',
  border: '1px solid rgba(255, 107, 107, 0.1)',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-6px)',
    border: '1px solid rgba(255, 107, 107, 0.28)',
    boxShadow: '0 12px 30px rgba(255, 107, 107, 0.12)',
  },
};

// ── AnimatedCircles component ─────────────────────────────────────────────────

function AnimatedCircles({ navigate }) {
  return (
    <Box sx={{ position: 'relative', width: 400, height: 400 }}>

      {/* Ripple / sonar rings — pulse outward from centre */}
      {[0, 1.2, 2.4].map((delay, i) => (
        <Box key={`rpl-${i}`} sx={{
          position: 'absolute',
          top: '50%', left: '50%',
          width: 360, height: 360,
          ml: '-180px', mt: '-180px',
          borderRadius: '50%',
          border: '1.5px solid rgba(255,107,107,0.38)',
          animation: `${rippleOut} 3.6s ease-out infinite`,
          animationDelay: `${delay}s`,
          pointerEvents: 'none',
          zIndex: 0,
        }} />
      ))}

      {/* Outermost dashed orbit — rotates CW with 4 glowing nodes */}
      <Box sx={{
        position: 'absolute',
        top: '50%', left: '50%',
        width: 420, height: 420,
        ml: '-210px', mt: '-210px',
        borderRadius: '50%',
        border: '1.5px dashed rgba(255,107,107,0.22)',
        animation: `${rotateCW} 32s linear infinite`,
        pointerEvents: 'none',
        zIndex: 1,
      }}>
        {[
          { top: '-5px',    left: '50%',  transform: 'translateX(-50%)' },
          { bottom: '-5px', left: '50%',  transform: 'translateX(-50%)' },
          { left: '-5px',   top: '50%',   transform: 'translateY(-50%)' },
          { right: '-5px',  top: '50%',   transform: 'translateY(-50%)' },
        ].map((pos, idx) => (
          <Box key={idx} sx={{
            position: 'absolute',
            ...pos,
            width: 10, height: 10,
            borderRadius: '50%',
            background: 'radial-gradient(circle, #ff6b6b 0%, rgba(255,107,107,0.25) 100%)',
            animation: `${orbitDotPulse} 2.4s ease-in-out infinite`,
            animationDelay: `${idx * 0.6}s`,
          }} />
        ))}
      </Box>

      {/* Radar sweep 1 — CW, red */}
      <Box sx={{
        position: 'absolute',
        top: '50%', left: '50%',
        width: 360, height: 360,
        ml: '-180px', mt: '-180px',
        borderRadius: '50%',
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 1,
      }}>
        <Box sx={{
          width: '100%', height: '100%',
          background: 'conic-gradient(from 0deg, transparent 0deg, rgba(255,107,107,0.14) 50deg, transparent 70deg)',
          animation: `${rotateCW} 5s linear infinite`,
        }} />
      </Box>

      {/* Radar sweep 2 — CCW, golden */}
      <Box sx={{
        position: 'absolute',
        top: '50%', left: '50%',
        width: 360, height: 360,
        ml: '-180px', mt: '-180px',
        borderRadius: '50%',
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 1,
      }}>
        <Box sx={{
          width: '100%', height: '100%',
          background: 'conic-gradient(from 180deg, transparent 0deg, rgba(255,217,61,0.09) 40deg, transparent 60deg)',
          animation: `${rotateCCW} 8s linear infinite`,
        }} />
      </Box>

      {/* Outer ring — pulses */}
      <Box sx={{
        position: 'absolute',
        top: '50%', left: '50%',
        width: 360, height: 360,
        ml: '-180px', mt: '-180px',
        borderRadius: '50%',
        border: '1px solid rgba(255,107,107,0.15)',
        animation: `${ringPulse} 4s ease-in-out infinite`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 2,
      }}>

        {/* Middle ring with CCW dashed overlay */}
        <Box sx={{
          width: 270, height: 270,
          borderRadius: '50%',
          border: '1px solid rgba(255,107,107,0.09)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          position: 'relative',
        }}>
          <Box sx={{
            position: 'absolute',
            width: '100%', height: '100%',
            borderRadius: '50%',
            border: '1.5px dashed rgba(255,217,61,0.22)',
            animation: `${rotateCCW} 18s linear infinite`,
            pointerEvents: 'none',
          }} />

          {/* Spinning rainbow gradient border + inner core */}
          <Box sx={{
            position: 'relative',
            width: 186, height: 186,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {/* The spinning colour ring */}
            <Box sx={{
              position: 'absolute',
              top: 0, right: 0, bottom: 0, left: 0,
              borderRadius: '50%',
              background: 'conic-gradient(from 0deg, #ff6b6b, #ffd93d, #6ba3ff, #ff6b6b)',
              animation: `${rotateCW} 4s linear infinite`,
              WebkitMaskImage: 'radial-gradient(circle at center, transparent 85px, black 85.5px)',
              maskImage: 'radial-gradient(circle at center, transparent 85px, black 85.5px)',
              opacity: 0.8,
            }} />

            {/* Inner circle — glow pulse + breathe */}
            <Box sx={{
              width: 170, height: 170,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(255,107,107,0.22) 0%, rgba(255,217,61,0.1) 100%)',
              border: '1.5px solid rgba(255,107,107,0.35)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              animation: `${glowPulse} 3s ease-in-out infinite, ${scaleBreath} 4s ease-in-out infinite`,
              position: 'relative', zIndex: 1,
            }}>
              <DevicesIcon sx={{
                fontSize: 68,
                color: 'primary.main',
                animation: `${iconFloat} 3s ease-in-out infinite`,
              }} />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Floating badges (clickable links) */}
      {badges.map((badge, i) => (
        <Box
          key={i}
          onClick={() => navigate(badge.route)}
          sx={{
            position: 'absolute',
            top: badge.top,
            bottom: badge.bottom,
            left: badge.left,
            right: badge.right,
            background: 'rgba(30,36,38,0.96)',
            border: '1px solid rgba(255,107,107,0.28)',
            borderRadius: '12px',
            px: 1.6, py: 0.9,
            display: 'flex', alignItems: 'center', gap: 0.8,
            cursor: 'pointer',
            zIndex: 10,
            animation: `${floatBadge} 3.2s ease-in-out infinite, ${badgeGlow} 3.2s ease-in-out infinite`,
            animationDelay: `${badge.delay}, ${badge.delay}`,
            transition: 'background 0.25s ease, border-color 0.25s ease, transform 0.25s ease',
            '&:hover': {
              animationPlayState: 'paused',
              transform: 'translateY(-6px) scale(1.08)',
              background: 'rgba(255,107,107,0.18)',
              borderColor: 'rgba(255,107,107,0.65)',
              boxShadow: '0 10px 28px rgba(255,107,107,0.3)',
            },
          }}
        >
          {React.createElement(badge.icon, { sx: { fontSize: 15, color: 'primary.main' } })}
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.72rem', letterSpacing: 0.5 }}>
            {badge.label}
          </Typography>
          <ArrowForwardIcon sx={{ fontSize: 11, color: 'primary.main', opacity: 0.75 }} />
        </Box>
      ))}
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

function Services() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState(null);

  if (activeCategory !== null) {
    const category = categories[activeCategory];
    return (
      <Box sx={{ minHeight: '100vh', py: 10, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{
          position: 'absolute', top: '8%', right: '5%',
          width: 380, height: 380,
          background: 'radial-gradient(circle, rgba(255,107,107,0.1) 0%, transparent 70%)',
          borderRadius: '50%', pointerEvents: 'none',
        }} />
        <Box sx={{
          position: 'absolute', bottom: '10%', left: '2%',
          width: 260, height: 260,
          background: 'radial-gradient(circle, rgba(255,217,61,0.06) 0%, transparent 70%)',
          borderRadius: '50%', pointerEvents: 'none',
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => setActiveCategory(null)}
            sx={{
              color: 'primary.main',
              mb: 5,
              fontWeight: 600,
              '&:hover': { background: 'rgba(255,107,107,0.08)' },
            }}
          >
            Back to Services
          </Button>

          <Typography
            variant="overline"
            sx={{ color: 'primary.main', fontWeight: 600, letterSpacing: 3, fontSize: '0.85rem', display: 'block', mb: 1 }}
          >
            {category.title}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{
              width: 56, height: 56,
              borderRadius: '14px',
              background: 'rgba(255, 107, 107, 0.1)',
              border: '1px solid rgba(255,107,107,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              {React.createElement(category.icon, { sx: { fontSize: 28, color: 'primary.main' } })}
            </Box>
            <Typography variant="h2" className="gradient-text" sx={{ fontWeight: 700 }}>
              {category.title}
            </Typography>
          </Box>

          <Typography variant="h6" color="text.secondary" sx={{ mb: 8, maxWidth: 600, lineHeight: 1.8 }}>
            {category.description}
          </Typography>

          <Grid container spacing={3}>
            {category.services.map((service, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card sx={cardStyle} onClick={() => navigate(service.path)}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{
                      width: 52, height: 52,
                      borderRadius: '12px',
                      background: 'rgba(255, 107, 107, 0.1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      mb: 2,
                    }}>
                      {React.createElement(service.icon, { sx: { fontSize: 26, color: 'primary.main' } })}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#fff', mb: 1 }}>
                      {service.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75, mb: 2 }}>
                      {service.description}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'primary.main' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>Learn More</Typography>
                      <ArrowForwardIcon sx={{ fontSize: 14 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', position: 'relative', overflow: 'hidden' }}>
      <Box sx={{
        position: 'absolute', top: '12%', right: '6%',
        width: 440, height: 440,
        background: 'radial-gradient(circle, rgba(255,107,107,0.12) 0%, transparent 70%)',
        borderRadius: '50%', pointerEvents: 'none',
      }} />
      <Box sx={{
        position: 'absolute', bottom: '15%', left: '2%',
        width: 300, height: 300,
        background: 'radial-gradient(circle, rgba(255,217,61,0.07) 0%, transparent 70%)',
        borderRadius: '50%', pointerEvents: 'none',
      }} />

      {/* Hero */}
      <Box sx={{
        minHeight: '88vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        zIndex: 1,
        borderBottom: '1px solid rgba(255,107,107,0.08)',
      }}>
        <Container>
          <Grid container spacing={4} alignItems="center">
            {/* Left: text + buttons */}
            <Grid item xs={12} md={7}>
              <Typography
                variant="overline"
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  letterSpacing: 3,
                  fontSize: '0.85rem',
                  display: 'block',
                  mb: 2,
                }}
              >
                Lynn, MA · Professional IT Services
              </Typography>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.4rem', md: '3.8rem' },
                  fontWeight: 800,
                  lineHeight: 1.15,
                  mb: 3,
                  color: '#fff',
                }}
              >
                Enterprise IT{' '}
                <Box component="span" className="gradient-text">
                  For Every Need
                </Box>
              </Typography>
              <Typography
                variant="h6"
                sx={{ color: 'text.secondary', mb: 4, fontWeight: 400, maxWidth: 520, lineHeight: 1.8 }}
              >
                From hardware repair to cloud infrastructure — 16 services across 4 categories, backed by 16+ years of enterprise experience.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => document.getElementById('service-categories').scrollIntoView({ behavior: 'smooth' })}
                  sx={{
                    background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)',
                    color: '#2d3436',
                    fontWeight: 700,
                    px: 4, py: 1.5,
                    fontSize: '1rem',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, var(--primary) 20%, var(--secondary) 80%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 20px rgba(255,107,107,0.3)',
                    },
                  }}
                >
                  Explore Services
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/book-service')}
                  sx={{
                    borderColor: 'rgba(255,107,107,0.5)',
                    color: '#fff',
                    px: 4, py: 1.5,
                    fontSize: '1rem',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      background: 'rgba(255,107,107,0.08)',
                    },
                  }}
                >
                  Book a Service
                </Button>
              </Box>
            </Grid>

            {/* Right: animated circles */}
            <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
              <AnimatedCircles navigate={navigate} />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats bar */}
      <Box sx={{
        background: 'rgba(255,107,107,0.04)',
        borderBottom: '1px solid rgba(255,107,107,0.1)',
        py: 4,
        position: 'relative', zIndex: 1,
      }}>
        <Container>
          <Grid container spacing={2} justifyContent="center">
            {[
              { value: '16', label: 'Services Available' },
              { value: '4',  label: 'Service Categories' },
              { value: 'CySA+', label: 'CompTIA Certified' },
              { value: '24/7', label: 'Support Available' },
            ].map((stat, i) => (
              <Grid item xs={6} sm={3} key={i} sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main' }}>{stat.value}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{stat.label}</Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Category cards */}
      <Box id="service-categories" sx={{ py: 10, position: 'relative', zIndex: 1 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {categories.map((category, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Card sx={cardStyle} onClick={() => category.path ? navigate(category.path) : setActiveCategory(index)}>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{
                      width: 64, height: 64,
                      borderRadius: '16px',
                      background: 'rgba(255, 107, 107, 0.1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      mb: 3,
                    }}>
                      {React.createElement(category.icon, { sx: { fontSize: 32, color: 'primary.main' } })}
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff', mb: 1.5 }}>
                      {category.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mb: 3 }}>
                      {category.description}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>
                        {category.services.length} services
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'primary.main' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Explore</Typography>
                        <ArrowForwardIcon sx={{ fontSize: 15 }} />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}

export default Services;
